import { defaultFiltersState, FiltersState } from '../../hooks/useFilters';
import type { SourceSwitchParams, SourceTransferParams } from '../sourceModules/types';
import {
  mapHhEducationToAvitoEducation,
  mapHhTotalExperienceToAvitoExperience,
  mapHhWorkFormatToAvitoSchedule,
} from './filterMappings';
import {
  hasAvitoExperienceValue,
  normalizeAvitoExperienceRange,
} from './experienceRange';
import { getAvitoAndSkillsFromSource } from './vacancyMapper';

type FilterKey = keyof FiltersState;

export const AVITO_SOURCE = 'avito';
export const AVITO_FILTER_BUCKET_ID = 'avito';

const AVITO_BOUNDARY_TRANSFER_KEYS: FilterKey[] = [
  'positionName',
  'salaryFrom',
  'salaryTo',
  'incomeSpecified',
  'hasSalary',
  'ageFrom',
  'ageTo',
  'gender',
  'strictSearch',
];

const AVITO_ONLY_KEYS: FilterKey[] = [
  'avitoEducationLevel',
  'avitoSchedule',
  'avitoBusinessTrip',
  'avitoRelocation',
  'avitoKeywords',
  'avitoExcludedWords',
  'avitoExperienceMin',
  'avitoExperienceMax',
  'avitoDriverLicence',
  'avitoDriverLicenceCategory',
  'avitoDrivingExperience',
  'avitoOwnTransport',
  'avitoMedicalBook',
  'avitoUpdatedFrom',
  'avitoUpdatedTo',
];

const AVITO_INCOMPATIBLE_KEYS: FilterKey[] = [
  'excludeUpdatedPeriod',
  'salaryCurrency',
  'education',
  'workFormat',
  'metroCityId',
  'metro',
  'onlyRussia',
  'includeNoAge',
  'stopCompanies',
  'donorCompanies',
  'companyFilterMode',
  'vacancyDescription',
  'atsVacancyId',
  'atsVacancyTitle',
  'expectedPositionName',
  'experiencePeriod',
  'skillsAny',
  'skillsAll',
  'skillGroups',
  'totalExperience',
  'positionExperience',
  'jobSearchStatus',
  'employmentType',
  'employmentTypes',
  'industry',
  'specialization',
  'selectedIndustries',
  'strictRegionSearch',
  'excludeJumpers',
  'hideViewedByCompany',
  'filterByExpIndustry',
  'currentCompany',
  'previousCompany',
  'currentPositionOnly',
  'hasPhoto',
  'updatedAfter',
  'updatedBefore',
  'createdAfter',
  'createdBefore',
];

const AVITO_SERIALIZABLE_KEYS: FilterKey[] = [
  'source',
  'limit',
  'sortBy',
  'page',
  'pageSize',
  'salaryFrom',
  'salaryTo',
  'incomeSpecified',
  'hasSalary',
  'cities',
  'cityIds',
  'ageFrom',
  'ageTo',
  'gender',
  'citizenship',
  'positionName',
  'searchMode',
  'strictSearch',
  'scoringEnabled',
  'selectedSpecializations',
  ...AVITO_ONLY_KEYS,
];

const resetKeysToDefault = (target: FiltersState, keys: FilterKey[]) => {
  keys.forEach((key) => {
    (target as any)[key] = (defaultFiltersState as any)[key];
  });
};

const areEqualValues = (left: unknown, right: unknown): boolean => (
  JSON.stringify(left) === JSON.stringify(right)
);

const isEmptySerializableValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const hasFilledValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim() !== '';
  return value !== undefined && value !== null;
};

export function clearAvitoOwnedFields(filters: FiltersState): void {
  resetKeysToDefault(filters, AVITO_ONLY_KEYS);
}

function getAvitoMappedTransferableFilters(
  currentFilters: Partial<FiltersState>,
): Partial<FiltersState> {
  const experience = mapHhTotalExperienceToAvitoExperience(currentFilters.totalExperience);
  const education = mapHhEducationToAvitoEducation(currentFilters.education);
  const hasWorkFormat = hasFilledValue(currentFilters.workFormat);
  const schedule = hasWorkFormat
    ? mapHhWorkFormatToAvitoSchedule(currentFilters.workFormat)
    : undefined;
  const avitoKeywords = getAvitoAndSkillsFromSource(currentFilters);

  return {
    ...(experience || {}),
    ...(education.length > 0 ? { avitoEducationLevel: education } : {}),
    ...(schedule ? { avitoSchedule: schedule } : {}),
    ...(avitoKeywords.length > 0 ? { avitoKeywords } : {}),
  };
}

export function getAvitoTransferableFilters({
  currentFilters,
}: SourceTransferParams): Partial<FiltersState> {
  const next = AVITO_BOUNDARY_TRANSFER_KEYS.reduce<Partial<FiltersState>>((acc, key) => {
    const value = (currentFilters as any)[key];
    if (value !== undefined) {
      (acc as any)[key] = value;
    }
    return acc;
  }, {});

  return {
    ...next,
    ...getAvitoMappedTransferableFilters(currentFilters),
  };
}

export function sanitizeAvitoFilters(filters: Partial<FiltersState> = {}): FiltersState {
  const next = {
    ...defaultFiltersState,
    ...filters,
    source: AVITO_SOURCE,
  } as FiltersState;
  const experience = normalizeAvitoExperienceRange(next);

  resetKeysToDefault(next, AVITO_INCOMPATIBLE_KEYS);
  next.avitoExperienceMin = experience.avitoExperienceMin;
  next.avitoExperienceMax = experience.avitoExperienceMax;

  return next;
}

export const getDefaultAvitoFilters = (): FiltersState => (
  sanitizeAvitoFilters({
    ...defaultFiltersState,
    source: AVITO_SOURCE,
    strictSearch: false,
  })
);

export function getSerializableAvitoFilters(
  filters: Partial<FiltersState> = {},
): Partial<FiltersState> {
  const sanitized = sanitizeAvitoFilters(filters);
  const defaults = getDefaultAvitoFilters();
  const serializable: Partial<FiltersState> = {};

  AVITO_SERIALIZABLE_KEYS.forEach((key) => {
    const value = (sanitized as any)[key];
    if (key === 'source') {
      (serializable as any)[key] = sanitized.source;
      return;
    }
    if (isEmptySerializableValue(value)) return;
    if (areEqualValues(value, (defaults as any)[key])) return;
    (serializable as any)[key] = value;
  });

  return serializable;
}

export function buildAvitoFiltersForSourceSwitch({
  currentFilters,
  savedTargetFilters,
}: SourceSwitchParams): FiltersState {
  if (savedTargetFilters) {
    const saved = sanitizeAvitoFilters({
      ...savedTargetFilters,
      source: AVITO_SOURCE,
      page: 1,
    });
    const mapped = getAvitoMappedTransferableFilters(currentFilters);

    return sanitizeAvitoFilters({
      ...saved,
      ...mapped,
    });
  }

  return sanitizeAvitoFilters({
    ...getDefaultAvitoFilters(),
    ...getAvitoTransferableFilters({
      fromSource: currentFilters.source || defaultFiltersState.source,
      toSource: AVITO_SOURCE,
      currentFilters,
    }),
    source: AVITO_SOURCE,
    page: 1,
  });
}

export function getActiveAvitoFiltersCount(filters: FiltersState): number {
  let count = 0;

  if (filters.avitoEducationLevel?.length) count++;
  if (filters.avitoSchedule?.length) count++;
  if (filters.avitoBusinessTrip?.length) count++;
  if (filters.avitoRelocation?.length) count++;
  if (filters.avitoKeywords?.length) count++;
  if (filters.avitoExcludedWords?.length) count++;
  if (hasAvitoExperienceValue(filters.avitoExperienceMin) || hasAvitoExperienceValue(filters.avitoExperienceMax)) count++;
  if (filters.avitoDriverLicence) count++;
  if (filters.avitoDriverLicenceCategory?.length) count++;
  if (filters.avitoDrivingExperience?.length) count++;
  if (filters.avitoOwnTransport?.length) count++;
  if (filters.avitoMedicalBook) count++;
  if (filters.avitoUpdatedFrom || filters.avitoUpdatedTo) count++;

  return count;
}
